import React, { useCallback, useState, useMemo } from 'react';
import { Box } from '@tanium/react-box';
import { TooltippedButton } from '@tanium/react-button';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { TextInput } from '@tanium/react-text-input';
import { TrashIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { SectionContainer } from '@tanium/react-section-container';
import { Stack, StackChild } from '@tanium/react-stack';
import { StackedRibbonListMinimalStatic, StackedRibbonListWrapper } from '@tanium/react-stacked-ribbon-list';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps, FormikProps } from 'formik';
import { useOnInputChange } from '../InputHandlerUtils';
import { alwaysTrueOp } from '../../utilities/commonFuncs';
import { orderBy } from '../../utilities/arrayHelper';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { useTenantDomains } from '../../hooks/useTenantDomains';
import { Domain, DomainStatus } from '../../types';
import { MediumSubtleText } from '../../TextStyles';

interface LoginDomainFormProps {
  formData: FormikProps<ConfigureIdpRequest>;
}

const IDP_DOMAIN_REGEX = /^[a-z0-9][a-z0-9-.]*$/;
const INVALID_DOMAINS = ['tanium.local', 'mytanium.com'];

interface AssignableDomain {
  label: string;
  value: Domain;
  disabled: boolean;
}

export const LoginDomainForm: React.VFC<LoginDomainFormProps> = ({ formData }) => {
  const { tenantDomains } = useTenantDomains();
  const { idpConfigs } = useIdentityProviderConfigs();

  const availableDomains = useMemo(() => {
    return tenantDomains.map<AssignableDomain>((domain) => {
      return {
        label: domain.domainName,
        value: domain,
        disabled: domain.verificationStatus !== DomainStatus.SUCCESS,
      };
    });
  }, [tenantDomains]);

  const [selectedDomain, setSelectedDomain] = useState<undefined | AssignableDomain>();
  const [subdomain, setSubdomain] = useState<string | undefined>();
  const [validationError, setValidationError] = useState('');
  const domainInput = useMemo(() => {
    const domain = selectedDomain?.value.domainName || '';
    if (domain && subdomain) {
      return `${subdomain}.${domain}`;
    }
    return domain;
  }, [selectedDomain?.value.domainName, subdomain]);

  const domains = useMemo(() => {
    return orderBy(formData.values.domainNames, (a, b) => (a > b ? 1 : -1));
  }, [formData.values.domainNames]);

  const { t } = useTranslation();

  const onAddDomain = useCallback(
    (domain: string) => {
      formData.setFieldValue('domainNames', [...formData.values.domainNames, domain]);
    },
    [formData]
  );
  const onRemoveDomain = useCallback(
    (domain: string) => {
      formData.setFieldValue(
        'domainNames',
        formData.values.domainNames.filter((x) => x !== domain)
      );
      // need to validate at least one domain is required when domain is deleted
      // Can't use yup.min validation because it would suppress invalid domain error when first domain is invalid
      if (domains.length <= 1) {
        setValidationError(t('At least one Sign In Email Domain must be provided'));
      }
    },
    [domains.length, formData, t]
  );

  const idpAlreadyUsesDomain = useCallback(
    (domain: string): boolean => {
      const usedDomains = idpConfigs.filter((idp) => idp.id !== formData.values.id).flatMap((x) => x.domainNames);
      const existDomain = [...domains, ...usedDomains].find((x) => x === domain);
      return existDomain !== undefined;
    },
    [domains, formData.values.id, idpConfigs]
  );

  const addSignInDomainTooltip = useMemo((): string => {
    if (idpAlreadyUsesDomain(domainInput)) {
      return t('The selected domain has already been added.');
    }
    return '';
  }, [domainInput, idpAlreadyUsesDomain, t]);

  const isValidDomain = useMemo((): boolean => {
    if (!domainInput || idpAlreadyUsesDomain(domainInput)) {
      // disable Add Domain button if domainInput is empty or already added
      return false;
    }
    const domainRegex = new RegExp(IDP_DOMAIN_REGEX);
    if (!domainRegex.test(domainInput) || INVALID_DOMAINS.includes(domainInput)) {
      const invalidDomainMsg = t('Invalid domain');
      if (validationError !== invalidDomainMsg) {
        setValidationError(t('Invalid domain'));
      }
      return false;
    }
    return true;
  }, [domainInput, idpAlreadyUsesDomain, t, validationError]);

  const onAddDomainHandler = useCallback(() => {
    onAddDomain(domainInput);
    setSubdomain('');
    setValidationError('');
  }, [domainInput, onAddDomain]);

  const onSubdomainChange = useOnInputChange((sub) => setSubdomain(sub.toLocaleLowerCase()));

  return (
    <>
      <Stack key={0} direction="row" itemSpacing={2}>
        <StackChild grow={1}>
          <SectionContainer
            sideContent={
              <>
                <FormRow label={t('Preview')}>
                  <TextInput style={{ width: '300px' }} value={domainInput} disabled={true} />
                </FormRow>
                <TooltippedButton
                  disabled={!isValidDomain}
                  tooltipContent={addSignInDomainTooltip}
                  onClick={onAddDomainHandler}
                >
                  {t('Add Sign In Domain')}
                </TooltippedButton>
              </>
            }
          >
            <Field name="domainNames">
              {({ field }: FieldProps): React.ReactElement => (
                <FormRow {...field} dataKey="domainNames" label={t('Domain')} errors={validationError} required>
                  <FormSelectInput
                    {...field}
                    data-testid="domain-select"
                    value={selectedDomain}
                    options={availableDomains}
                    onChange={(value: AssignableDomain) => setSelectedDomain(value)}
                    isOptionDisabled={/* istanbul ignore next */ (option: AssignableDomain) => option.disabled}
                  />
                  <br />
                  <FormRow label={t('Subdomain')} helpText={t('Optional')}>
                    <FormTextInput data-testid="subdomain-input" onChange={onSubdomainChange} value={subdomain} />
                  </FormRow>
                </FormRow>
              )}
            </Field>
          </SectionContainer>
        </StackChild>
      </Stack>

      <Box>
        <MediumSubtleText>
          {t(
            'Select a verified domain, and optionally provide a subdomain to specify the email domains that can be used to sign in.'
          )}
        </MediumSubtleText>
        <br />
        <br />
        {domains.length > 0 && (
          <StackedRibbonListWrapper>
            <StackedRibbonListMinimalStatic
              columns={[
                {
                  label: t('Domain'),
                  dataKey: 'domain',
                  isSortDisabled: true,
                  renderer: ({ itemData }: { itemData: string }) => {
                    return itemData;
                  },
                },
                {
                  label: t('Action'),
                  dataKey: 'action',
                  isSortDisabled: true,
                  width: 80,
                  renderer: ({ itemData }: { itemData: string }) => (
                    <IconButton
                      icon={TrashIcon}
                      height={16}
                      width={16}
                      label={t('Remove')}
                      onClick={() => {
                        onRemoveDomain(itemData);
                      }}
                    />
                  ),
                },
              ]}
              dataGetter={(i) => domains[i]}
              isItemLoaded={alwaysTrueOp}
              itemCount={domains.length}
              itemIdGetter={(i) => domains[i]}
            ></StackedRibbonListMinimalStatic>
          </StackedRibbonListWrapper>
        )}
      </Box>
    </>
  );
};
